/**
 * Campaign and connect platform marks.
 *
 * Required posting platforms come from `measurement_routes`. Sandbox is a
 * test route and is hidden when a real network is also listed.
 */
import { fontSize } from "@design/index";

export interface PlatformMeta {
  readonly id: string;
  readonly label: string;
  readonly src: string;
}

const ICONS: Record<string, PlatformMeta> = {
  tiktok: { id: "tiktok", label: "TikTok", src: "/icons/tiktok.png" },
  instagram: { id: "instagram", label: "Instagram", src: "/icons/instagram.png" },
  facebook: { id: "facebook", label: "Facebook", src: "/icons/facebook.png" },
  reddit: { id: "reddit", label: "Reddit", src: "/icons/reddit.png" },
  youtube: { id: "youtube", label: "YouTube", src: "/icons/youtube.svg" },
};

export const KNOWN_PLATFORMS: readonly PlatformMeta[] = [
  ICONS.tiktok, ICONS.instagram, ICONS.facebook, ICONS.reddit, ICONS.youtube,
] as PlatformMeta[];

/** Public profile URL for a connected handle, so a brand can open it. */
export function profileUrl(platform: string, handle: string | null | undefined): string | null {
  if (!handle) return null;
  const h = handle.replace(/^@/, "").trim();
  if (!h) return null;
  switch (platform.trim().toLowerCase()) {
    case "youtube": return `https://www.youtube.com/@${encodeURIComponent(h)}`;
    case "tiktok": return `https://www.tiktok.com/@${encodeURIComponent(h)}`;
    case "instagram": return `https://www.instagram.com/${encodeURIComponent(h)}`;
    case "facebook": return `https://www.facebook.com/${encodeURIComponent(h)}`;
    case "reddit": return `https://www.reddit.com/user/${encodeURIComponent(h)}`;
    default: return null;
  }
}

export function platformMeta(id: string): PlatformMeta {
  const key = id.trim().toLowerCase();
  return ICONS[key] ?? {
    id: key,
    label: key === "sandbox" ? "Test route" : key.replaceAll("_", " "),
    src: "",
  };
}

export function visiblePlatforms(routes: readonly string[] | null | undefined): PlatformMeta[] {
  const raw = (routes ?? []).map((r) => r.trim().toLowerCase()).filter(Boolean);
  const social = raw.filter((r) => r !== "sandbox").map(platformMeta);
  if (social.length) return social;
  return raw.map(platformMeta);
}

export function PlatformIcon({
  id, size = 22, title,
}: { id: string; size?: number; title?: string }) {
  const p = platformMeta(id);
  if (!p.src) {
    return (
      <span
        title={title ?? p.label}
        style={{
          width: size, height: size, borderRadius: "50%",
          display: "inline-grid", placeItems: "center",
          background: "var(--surface-3)", color: "var(--text-2)",
          fontSize: Math.max(9, size * 0.38), fontWeight: 700,
        }}
      >
        {p.label.slice(0, 1).toUpperCase()}
      </span>
    );
  }
  return (
    <img
      src={p.src}
      alt=""
      title={title ?? p.label}
      width={size}
      height={size}
      style={{ width: size, height: size, borderRadius: "50%", objectFit: "cover", display: "block" }}
    />
  );
}

export function PlatformRow({
  routes, size = 22, labeled = false,
}: {
  routes: readonly string[] | null | undefined;
  size?: number;
  labeled?: boolean;
}) {
  const items = visiblePlatforms(routes);
  if (items.length === 0) return null;
  return (
    <ul style={{
      margin: 0, padding: 0, listStyle: "none",
      display: "flex", flexWrap: "wrap", gap: labeled ? 8 : 6, alignItems: "center",
    }}>
      {items.map((p) => (
        <li key={p.id} style={{
          display: "inline-flex", alignItems: "center", gap: 6,
          ...(labeled ? {
            padding: "4px 8px 4px 4px",
            border: "1px solid var(--line)",
            borderRadius: 999,
            background: "var(--surface-2)",
          } : {}),
        }}>
          <PlatformIcon id={p.id} size={size} title={p.label} />
          {labeled ? (
            <span style={{ fontSize: fontSize.label, fontWeight: 650 }}>{p.label}</span>
          ) : (
            <span className="cm-sr-only">{p.label}</span>
          )}
        </li>
      ))}
    </ul>
  );
}
